// frontend/src/components/UI/EmergencyContactModal.js
import React, { useEffect, useRef } from 'react';
import PhoneNumberInput from './PhoneNumberInput';
import Portal from './Portal';
import { X, User, Users, Mail, AlertCircle, Save, UserPlus } from 'lucide-react';
import FormSelect from './FormSelect';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS } from '../../constants/emergencyContactRelationships';

/**
 * EmergencyContactModal - Modal for adding/editing emergency contacts
 *
 * theme="employee" (default) uses navy accents; theme="admin" uses red.
 */
const EmergencyContactModal = ({
  isOpen,
  onClose,
  formData,
  formErrors,
  editingContact,
  saving,
  isForeignEmployee,
  onInputChange,
  onSave,
  theme = 'employee',
}) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const isAdmin = theme === 'admin';

  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const safeFormData = {
    emergency_contact_name: formData?.emergency_contact_name || '',
    emergency_contact_relationship: formData?.emergency_contact_relationship || '',
    emergency_contact_relationship_other: formData?.emergency_contact_relationship_other || '',
    emergency_contact_primary_phone: formData?.emergency_contact_primary_phone || '',
    emergency_contact_secondary_phone: formData?.emergency_contact_secondary_phone || '',
    emergency_contact_email: formData?.emergency_contact_email || ''
  };

  const safeFormErrors = {};
  if (formErrors && typeof formErrors === 'object') {
    Object.keys(formErrors).forEach(key => {
      const errorValue = formErrors[key];
      safeFormErrors[key] = typeof errorValue === 'string' ? errorValue : String(errorValue || '');
    });
  }

  if (typeof onClose !== 'function' || typeof onSave !== 'function') {
    console.error('EmergencyContactModal: onClose and onSave must be functions');
    return null;
  }

  const iconAccent = isAdmin ? 'text-red-600' : 'text-blue-600';
  const labelIcon = isAdmin ? 'text-red-700' : 'text-blue-900';
  const inputClass = isAdmin
    ? 'input-admin'
    : 'input px-4 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-blue-500';
  const noticeWrap = isAdmin
    ? 'bg-red-50 border-b border-red-100 p-4'
    : 'bg-blue-50 border-b border-blue-200 p-4';
  const noticeTitle = isAdmin ? 'text-sm font-semibold text-red-900 mb-1' : 'text-sm font-semibold text-blue-900 mb-1';
  const noticeBody = isAdmin ? 'text-xs text-red-800 mb-2' : 'text-xs text-blue-800 mb-2';
  const noticeList = isAdmin ? 'text-xs text-red-800 space-y-1 list-disc list-inside mb-2' : 'text-xs text-blue-800 space-y-1 list-disc list-inside mb-2';
  const noticeNote = isAdmin ? 'text-xs text-red-700 italic' : 'text-xs text-blue-700 italic';
  const cancelClass = isAdmin ? 'btn btn-admin-outline' : 'btn btn-outline';
  const saveClass = isAdmin ? 'btn btn-admin-primary' : 'btn btn-primary';

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div
          ref={modalRef}
          className="modal-container modal-container-md"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <UserPlus className={`w-6 h-6 ${iconAccent}`} />
            <h3 className="modal-title">
              {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="modal-close"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isForeignEmployee && (
          <div className={noticeWrap}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 ${iconAccent} flex-shrink-0 mt-0.5`} />
              <div>
                <h4 className={noticeTitle}>Two Contacts Recommended</h4>
                <p className={noticeBody}>
                  For foreign employees, we recommend adding <strong>two separate contacts</strong>:
                </p>
                <ul className={noticeList}>
                  <li><strong>Contact 1 (Local):</strong> Someone in Singapore who can reach the hospital quickly</li>
                  <li><strong>Contact 2 (Home Country):</strong> Immediate family in your home country who needs to be informed</li>
                </ul>
                <p className={noticeNote}>
                  Note: Each contact can have a primary phone (required) and secondary phone (optional).
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="modal-content">
          <div className="space-y-6">
            <div>
              <label className="form-label flex items-center gap-2 mb-2">
                <User className={`w-4 h-4 ${labelIcon}`} />
                Full Name
                <span className="text-red-500">*</span>
              </label>
              <input
                ref={firstInputRef}
                type="text"
                name="emergency_contact_name"
                value={safeFormData.emergency_contact_name}
                onChange={onInputChange}
                placeholder="Enter full name"
                className={`${inputClass} ${
                  safeFormErrors.emergency_contact_name ? 'border-red-500' : ''
                }`}
                aria-required="true"
                aria-invalid={!!safeFormErrors.emergency_contact_name}
              />
              {safeFormErrors.emergency_contact_name && (
                <p className="form-error mt-1" role="alert">{safeFormErrors.emergency_contact_name}</p>
              )}
            </div>

            <div>
              <label className="form-label flex items-center gap-2 mb-2">
                <Users className={`w-4 h-4 ${labelIcon}`} />
                Relationship
                <span className="text-red-500">*</span>
              </label>
              <FormSelect
                value={safeFormData.emergency_contact_relationship}
                onChange={(val) => onInputChange({ target: { name: 'emergency_contact_relationship', value: val } })}
                options={[
                  { value: '', label: 'Select relationship' },
                  ...EMERGENCY_CONTACT_RELATIONSHIP_OPTIONS,
                ]}
                theme={theme}
              />
              {safeFormData.emergency_contact_relationship === 'parent' && (
                <p className="form-help">
                  This contact is saved as Parent. Please choose Father or Mother.
                </p>
              )}
              {safeFormErrors.emergency_contact_relationship && (
                <p className="form-error mt-1" role="alert">{safeFormErrors.emergency_contact_relationship}</p>
              )}
            </div>

            <PhoneNumberInput
              label="Primary Phone Number (Mobile)"
              name="emergency_contact_primary_phone"
              value={safeFormData.emergency_contact_primary_phone}
              onChange={onInputChange}
              error={safeFormErrors.emergency_contact_primary_phone}
              required={true}
              placeholder="Enter phone number"
              defaultCountry="SG"
            />

            <PhoneNumberInput
              label="Secondary Phone Number (Work/Home)"
              name="emergency_contact_secondary_phone"
              value={safeFormData.emergency_contact_secondary_phone}
              onChange={onInputChange}
              error={safeFormErrors.emergency_contact_secondary_phone}
              required={false}
              placeholder="Work line or home landline (optional)"
              defaultCountry="SG"
            />

            <div>
              <label className="form-label flex items-center gap-2 mb-2">
                <Mail className={`w-4 h-4 ${labelIcon}`} />
                Email (Optional)
              </label>
              <input
                type="email"
                name="emergency_contact_email"
                value={safeFormData.emergency_contact_email}
                onChange={onInputChange}
                placeholder="Enter email address"
                className={`${inputClass} ${
                  safeFormErrors.emergency_contact_email ? 'border-red-500' : ''
                }`}
                aria-invalid={!!safeFormErrors.emergency_contact_email}
              />
              {safeFormErrors.emergency_contact_email && (
                <p className="form-error mt-1" role="alert">{safeFormErrors.emergency_contact_email}</p>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            className={cancelClass}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className={saveClass}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </>
            )}
          </button>
        </div>
      </div>
      </div>
    </Portal>
  );
};

export default EmergencyContactModal;
